import { useEffect, useRef, useState } from 'react';
import { useEvalSets } from '../hooks/useEvalSets';

const SEARCH_DEBOUNCE_MS = 250;
const MAX_SUGGESTIONS = 20;

interface EvalSetFilterProps {
  value: string;
  onChange: (evalSetId: string) => void;
}

/**
 * Scope search to one eval set. A selected set renders as a removable chip; an
 * empty selection renders the typeahead. The typeahead (and its eval-sets fetch)
 * only mounts when nothing is selected, so a deep-linked `?eval_set=` page
 * doesn't issue a useless /meta/eval-sets request.
 */
export function EvalSetFilter({ value, onChange }: EvalSetFilterProps) {
  if (value) {
    return (
      <span className="badge gap-1.5 py-1">
        <span className="max-w-md truncate" title={value}>
          {value}
        </span>
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear eval set filter"
          className="opacity-70 hover:opacity-100"
        >
          ✕
        </button>
      </span>
    );
  }
  return <EvalSetTypeahead onChange={onChange} />;
}

/**
 * Typeahead over eval sets. There can be thousands, so this searches the backend
 * (`/meta/eval-sets?search=`) as you type rather than rendering them all.
 */
function EvalSetTypeahead({
  onChange,
}: {
  onChange: (evalSetId: string) => void;
}) {
  const [text, setText] = useState('');
  // `pushed` mirrors what's actually been sent to setSearch — gating both
  // `enabled` and the fetch on the same debounced value avoids a wasted first
  // request: without this, `enabled` would flip true on the first keystroke
  // while the hook's `search` is still empty, fetching the unfiltered default
  // listing before the debounced value lands.
  const [pushed, setPushed] = useState('');
  const [open, setOpen] = useState(false);
  const { evalSets, isLoading, setSearch } = useEvalSets({
    limit: MAX_SUGGESTIONS,
    enabled: pushed.length > 0,
  });
  const boxRef = useRef<HTMLDivElement>(null);

  // Debounce the typed text into the backend search. The empty-input reset is
  // done in the onChange handler instead of here, to keep the effect free of
  // synchronous setState (lint: react-hooks/set-state-in-effect).
  useEffect(() => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const handle = setTimeout(() => {
      setSearch(trimmed);
      setPushed(trimmed);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [text, setSearch]);

  // Close the suggestion list on outside click.
  useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  return (
    <div ref={boxRef} className="relative">
      <input
        type="search"
        value={text}
        onChange={e => {
          const next = e.target.value;
          setText(next);
          // Reset the debounced "pushed" value synchronously on clear so the
          // hook disables, aborts in-flight, and drops stale suggestions
          // without waiting for the next debounce tick.
          if (!next.trim()) setPushed('');
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        aria-label="Filter by eval set"
        placeholder="All eval sets — type to filter…"
        className="input h-7 w-72 max-w-full"
      />
      {/* Suggestion list states (mutually exclusive):
            - "Searching…" while the debounce hasn't fired (pushed!=text) OR a
              fetch is in flight — covers both first-keystroke and refining a
              query (during the new fetch, useEvalSets still holds the prior
              evalSets, so without this guard we'd show stale suggestions).
            - "No matching" only after a completed fetch for the current text.
            - Otherwise render evalSets — guaranteed to match the current input. */}
      {open && text.trim() && (
        <ul className="pane absolute z-10 mt-1 max-h-72 w-96 max-w-[calc(100vw-4rem)] overflow-auto py-1 text-xs shadow-lg">
          {(isLoading || pushed !== text.trim()) && (
            <li className="loading-placeholder px-3 py-1.5 text-muted">
              Searching…
            </li>
          )}
          {pushed === text.trim() && !isLoading && evalSets.length === 0 && (
            <li className="px-3 py-1.5 text-muted">No matching eval sets</li>
          )}
          {pushed === text.trim() &&
            !isLoading &&
            evalSets.map(es => (
              <li key={es.eval_set_id}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(es.eval_set_id);
                    setText('');
                    setOpen(false);
                  }}
                  className="block w-full truncate px-3 py-1.5 text-left text-ink-2 hover:bg-hover"
                  title={es.eval_set_id}
                >
                  {es.eval_set_id}
                </button>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}
